/**
 * D-126: totals of an invoice typed by hand, shown while it is typed. Same rule as the backend
 * (apps/billing/vat.py): whole FCFA, half-up rounding, the rate and « prices include VAT » come
 * from the agency's billing settings (never written in code). The backend recomputes on save.
 */

export interface InvoiceLineInput {
  quantity: number;
  unitPriceXaf: number;
}

export interface VatSettings {
  vatEnabled: boolean;
  /** Basis points: 1925 = 19.25 %. */
  vatRateBp: number;
  pricesIncludeVat: boolean;
}

export interface InvoiceTotals {
  gross: number;
  discount: number;
  ht: number;
  vat: number;
  ttc: number;
  rateBp: number;
}

function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5);
}

export function lineAmount(line: InvoiceLineInput): number {
  const quantity = Number.isFinite(line.quantity) && line.quantity > 0 ? Math.floor(line.quantity) : 0;
  const unit = Number.isFinite(line.unitPriceXaf) && line.unitPriceXaf > 0 ? Math.floor(line.unitPriceXaf) : 0;
  return quantity * unit;
}

export function invoiceTotals(lines: readonly InvoiceLineInput[], discountXaf: number, settings?: VatSettings): InvoiceTotals {
  const gross = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const discount = Math.max(0, Math.min(Math.floor(discountXaf || 0), gross));
  const amount = gross - discount;
  if (!settings || !settings.vatEnabled || settings.vatRateBp === 0) {
    return { gross, discount, ht: amount, vat: 0, ttc: amount, rateBp: 0 };
  }
  const rate = settings.vatRateBp / 10000;
  if (settings.pricesIncludeVat) {
    const ht = roundHalfUp(amount / (1 + rate));
    return { gross, discount, ht, vat: amount - ht, ttc: amount, rateBp: settings.vatRateBp };
  }
  // Integer arithmetic in basis points avoids floating errors on large amounts.
  const vat = Math.floor((amount * settings.vatRateBp + 5000) / 10000);
  return { gross, discount, ht: amount, vat, ttc: amount + vat, rateBp: settings.vatRateBp };
}

/** Splits a total into `count` instalments of whole FCFA; the last one takes the rest. */
export function splitInstalments(total: number, count: number): number[] {
  if (count <= 0 || total <= 0) return [];
  const part = Math.floor(total / count);
  return Array.from({ length: count }, (_, index) => (index === count - 1 ? total - part * (count - 1) : part));
}

/**
 * D-131: the payment provider's fee paid by the payer (Moneroo), added to the amount charged and
 * shown before payment on its own line. The rule is data sent by the API (`fee` of a sales method,
 * `online_fee` of the pay page); same arithmetic as apps/billing/fees.py: whole FCFA, half-up.
 * An exempt payer (EU/EEA consumer, country never charged) pays no fee.
 */
export interface PaymentFeeRuleInput {
  percent_bp: number;
  fixed_xaf: number;
  exempt: boolean;
}

export interface PaymentFeeBreakdown {
  amount: number;
  fee: number;
  total: number;
}

export function paymentFee(amountXaf: number, rule?: PaymentFeeRuleInput | null): number {
  const amount = Number.isFinite(amountXaf) ? Math.floor(amountXaf) : 0;
  if (!rule || rule.exempt || amount <= 0) return 0;
  const bp = Math.max(0, Math.floor(rule.percent_bp || 0));
  const fixed = Math.max(0, Math.floor(rule.fixed_xaf || 0));
  return fixed + Math.floor((amount * bp + 5000) / 10000);
}

export function withPaymentFee(amountXaf: number, rule?: PaymentFeeRuleInput | null): PaymentFeeBreakdown {
  const amount = Number.isFinite(amountXaf) && amountXaf > 0 ? Math.floor(amountXaf) : 0;
  const fee = paymentFee(amount, rule);
  return { amount, fee, total: amount + fee };
}
